import RehabFacilityInputGroup from 'components/intake/RehabFacilityInputGroup';
import { FormMeta, FormValues, PatientFormMode } from 'components/intake/types';
import AssignToMe from 'components/shared/form/AssignToMe';
import DatePicker from 'components/shared/form/DatePicker';
import FormSection from 'components/shared/form/FormSection';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { AFFILIATE, HOSPITAL, PHYSICIAN_TEAM } from 'constants/locationTypes';
import { useForm } from 'context/form';
import { useAddressOptions } from 'hooks/useAddressOptions';
import { parseDate } from 'lib/date';
import AttrValue from 'models/AttrValue';
import Classification, { ClassificationApiName } from 'models/Classification';
import Group from 'models/Group';
import { ManagerGroupTypeConfig } from 'models/GroupType';
import { PERMISSIONS } from 'models/Profile';
import Profile from 'models/Profile';
import User from 'models/User';
import { useInfiniteAttrValues } from 'services/api/attrValue';
import { useInfiniteClassifications } from 'services/api/classification';
import { useInfiniteGroups } from 'services/api/group';
import { useInfiniteUsers } from 'services/api/user';

export type EpisodeInfoSectionProps = {
  profile: Profile;
};

function EpisodeInfoSection({ profile }: EpisodeInfoSectionProps) {
  const { values, meta, errors, setValue, setValues } = useForm<FormValues, FormMeta>();

  const ownerConfig = values.owner?.groupType?.config as ManagerGroupTypeConfig;
  const showTransferredFromField = values.owner && !values.owner?.is(HOSPITAL);
  const transferredFromFieldIsOptional = !(ownerConfig?.patientTransferredFromRequired ?? true);
  const physicianIsOptional = !(ownerConfig?.patientPhysicianTeamRequired ?? true);

  const clientConfigForGroupType = profile?.actingClient.configForManagedGroupType(values.locationType?.value ?? '');

  // Can't use `profile.has(PERMISSIONS.locationEpisodeUserEdit)` here because Add/Coordinate form is not dealing with
  // an existing patient context, therefore we can't get check whether the profile is a manager for the patient.
  // Instead, we can check the permission explicitly and rely on the `isManagerUser` meta value to achieve the same effect.
  const canAssignUsers = profile.permissions[PERMISSIONS.locationEpisodeUserEdit] && meta.isManagerUser;
  const canAssignCaseManager =
    canAssignUsers && !!clientConfigForGroupType?.caseManager && !meta.latestLocationEpisodeIsArchived;
  const canAssignUtilizationManager =
    canAssignUsers && !!clientConfigForGroupType?.utilizationManager && !meta.latestLocationEpisodeIsArchived;

  const ownerIsSelected = values.owner?.id;
  const showHospitalDischargeDate = meta.mode == PatientFormMode.Connecting;

  const asyncEpisodeTypeOptions = useInfiniteClassifications({
    client: values.owner?.clientId,
    type: ClassificationApiName.EPISODE,
    sortBy: 'name',
  });

  const asyncPlanTypeOptions = useInfiniteClassifications({
    client: values.owner?.clientId,
    type: ClassificationApiName.PLAN_TYPE,
    sortBy: 'name',
  });

  const asyncPhysicianTeamOptions = useInfiniteAttrValues({
    type: PHYSICIAN_TEAM,
    distinctDisplayName: true,
  });

  const asyncUserOptions = useInfiniteUsers({
    group: values.owner?.id,
    include: 'credential',
    sortBy: 'name',
    active: true,
  });

  const asyncOwnerOptions = useInfiniteGroups({
    clientId: profile.actingClient.id,
    include: 'groupType',
    sortBy: 'name',
    isManager: true,

    'type.not': [AFFILIATE, PHYSICIAN_TEAM].join(','),
  });

  const asyncHospitalOptions = useInfiniteGroups({
    networkClientId: values.owner?.clientId,
    type: HOSPITAL,
    sortBy: 'name',
  });

  const addressProps = useAddressOptions();

  const handleOwnerChange = (change: Group) => {
    setValues(
      {
        caseManager: null,
        utilizationManager: null,
        owner: change,
      },
      { validate: true }
    );
  };

  return (
    <FormSection>
      <SectionHeader>Episode Information</SectionHeader>
      <InputGroup title='Hospital Admission Date (optional)'>
        <DatePicker
          maxDate={new Date()}
          selected={parseDate(values.admittedOn)}
          onChange={(change) => setValue('admittedOn', change?.toISOString())}
          disabled={!meta.isManagerUser}
        />
      </InputGroup>
      {showHospitalDischargeDate ? (
        <InputGroup title='Anticipated Hospital Discharge (optional)' error={errors.antHospitalDischarge}>
          <DatePicker
            selected={parseDate(values.antHospitalDischarge)}
            onChange={(change) => setValue('antHospitalDischarge', change?.toISOString())}
            disabled={false}
          />
        </InputGroup>
      ) : null}
      <InputGroup title='Managed By' error={errors.owner} data-cy='owner'>
        <Select<Group>
          {...asyncOwnerOptions}
          {...addressProps}
          name='owner'
          onChange={handleOwnerChange}
          value={values.owner}
          placeholder='Managed By'
          getOptionValue={(prop) => prop.id}
          hasError={!!errors.owner}
          disabled={!meta.isManagerUser}
        />
      </InputGroup>
      {showTransferredFromField && (
        <InputGroup
          title={`Hospital ${transferredFromFieldIsOptional ? '(optional)' : ''}`}
          error={errors.transferredFrom}
          data-cy='transferredFrom'>
          <Select<Group>
            {...asyncHospitalOptions}
            name='transferredFrom'
            placeholder='Hospital'
            value={values.transferredFrom}
            onChange={(value) => setValue('transferredFrom', value)}
            disabled={!meta.isManagerUser || !ownerIsSelected}
            getOptionValue={(prop) => prop.id}
            getOptionLabel={(prop) => prop.name}
            hasError={!!errors.transferredFrom}
            isClearable={transferredFromFieldIsOptional}
          />
        </InputGroup>
      )}
      <InputGroup
        title={`Physician ${physicianIsOptional ? '(optional)' : ''}`}
        error={errors.physicianTeam}
        data-cy='physicianTeam'>
        <Select<AttrValue>
          {...asyncPhysicianTeamOptions}
          name='physicianTeam'
          placeholder='Physician'
          value={values.physicianTeam}
          onChange={(change) => setValue('physicianTeam', change)}
          disabled={!meta.isManagerUser || !ownerIsSelected}
          getOptionValue={(prop) => prop.id}
          getOptionLabel={(prop) => prop.name}
          hasError={!!errors.physicianTeam}
          isClearable={physicianIsOptional}
        />
      </InputGroup>
      <RehabFacilityInputGroup profile={profile} />
      {canAssignUtilizationManager && (
        <InputGroup title='Utilization Manager (optional)' data-cy='utilizationManager'>
          <Select<User>
            {...asyncUserOptions}
            name='utilizationManager'
            value={values.utilizationManager}
            onChange={(value) => setValue('utilizationManager', value)}
            placeholder='Utilization Manager'
            getOptionValue={(prop) => prop.id}
            getOptionLabel={(prop) => prop.fullName}
            isClearable
          />
          {ownerIsSelected && <AssignToMe handleAssign={() => setValue('utilizationManager', profile)} />}
        </InputGroup>
      )}
      {canAssignCaseManager && (
        <InputGroup title='Case Manager (optional)' data-cy='caseManager'>
          <Select<User>
            {...asyncUserOptions}
            name='caseManager'
            value={values.caseManager}
            onChange={(value) => setValue('caseManager', value)}
            placeholder='Case Manager'
            getOptionValue={(prop) => prop.id}
            getOptionLabel={(prop) => prop.fullName}
            isClearable
          />
          {ownerIsSelected && <AssignToMe handleAssign={() => setValue('caseManager', profile)} />}
        </InputGroup>
      )}
      <SectionHeader>Plan Type + Episode Information</SectionHeader>
      <InputGroup title='Plan Type (optional)' data-cy='planTypeClassification'>
        <Select<Classification>
          {...asyncPlanTypeOptions}
          name='planTypeClassification'
          placeholder='Plan Type'
          onChange={(change) => setValue('planTypeClassification', change)}
          value={values.planTypeClassification}
          getOptionValue={(prop) => prop.id}
          getOptionLabel={(prop) => prop.name}
          disabled={!meta.isManagerUser || !ownerIsSelected}
          isClearable
        />
      </InputGroup>
      <InputGroup data-cy='episodeClassifications'>
        <MultiSelectChips<Classification>
          title='Episode Type (optional)'
          {...asyncEpisodeTypeOptions}
          name='episodeClassifications'
          values={values.episodeClassifications ?? []}
          onChange={(change) => setValue('episodeClassifications', change)}
          getOptionValue={(prop) => prop.id}
          getOptionLabel={(prop) => prop.name}
          disabled={!meta.isManagerUser || !ownerIsSelected}
          isClearable
        />
      </InputGroup>
    </FormSection>
  );
}

export default EpisodeInfoSection;
